import { useMemo } from 'react';
import { isZipUpTo5Digits, isValidIPv4 } from '../utils/testVendorRequest';
import type { Handler } from '../types/testVendorRequest.types';

interface ValidationError {
  show: boolean;
  message: string;
  variant: 'danger' | 'warning' | 'info';
  testId: string;
}

interface UseTestPostValidationOptions {
  localUrl: string;
  localHandler: Handler | number | string | null;
  visibleNames: string[];
  overrides: Record<string, string>;
  isSending: boolean;
}

interface ValidationResult {
  missingUrl: boolean;
  missingHandler: boolean;
  hasInvalidZip: boolean;
  invalidZipNames: string[];
  hasInvalidIp: boolean;
  invalidIpNames: string[];
  canSend: boolean;
  validationErrors: ValidationError[];
}

/**
 * Custom hook for TestPostModal validation logic.
 */
export const useTestPostValidation = ({
  localUrl,
  localHandler,
  visibleNames,
  overrides,
  isSending
}: UseTestPostValidationOptions): ValidationResult => {

  const missingUrl = !localUrl?.trim();
  const missingHandler = !localHandler;
  const canSend = !missingUrl && !missingHandler && !isSending;

  const invalidZipNames = useMemo<string[]>(() => {
    const namesToCheck = ['zip', 'postal_code', 'zip_code'];
    const present = new Set(visibleNames || []);
    const bad: string[] = [];
    namesToCheck.forEach((name) => {
      if (present.has(name)) {
        const value = (overrides?.[name] ?? '').trim();
        if (value !== '' && !isZipUpTo5Digits(value)) bad.push(name);
      }
    });
    return bad;
  }, [visibleNames, overrides]);

  const hasInvalidZip = invalidZipNames.length > 0;

  const invalidIpNames = useMemo<string[]>(() => {
    const namesToCheck = ['ip', 'ip_address', 'ipAddress', 'ip_addr'];
    const present = new Set(visibleNames || []);
    const bad: string[] = [];
    namesToCheck.forEach((name) => {
      if (present.has(name)) {
        const value = (overrides?.[name] ?? '').trim();
        if (value !== '' && !isValidIPv4(value)) bad.push(name);
      }
    });
    return bad;
  }, [visibleNames, overrides]);

  const hasInvalidIp = invalidIpNames.length > 0;

  const validationErrors = useMemo<ValidationError[]>(() => [
    {
      show: missingUrl,
      message: 'URL template is missing.',
      variant: 'danger',
      testId: 'err-missing-url',
    },
    {
      show: missingHandler,
      message: 'Success handler is not selected.',
      variant: 'danger',
      testId: 'err-missing-handler',
    },
    {
      show: hasInvalidZip,
      message: `ZIP code must be 1–5 digits for: ${invalidZipNames.join(', ')}`,
      variant: 'warning',
      testId: 'err-invalid-zip',
    },
    {
      show: hasInvalidIp,
      message: `IP address must be a valid IPv4 format (e.g., 192.168.1.10) for: ${invalidIpNames.join(', ')}`,
      variant: 'warning',
      testId: 'err-invalid-ip',
    },
  ], [missingUrl, missingHandler, hasInvalidZip, invalidZipNames, hasInvalidIp, invalidIpNames]);

  return {
    missingUrl,
    missingHandler,
    hasInvalidZip,
    invalidZipNames,
    hasInvalidIp,
    invalidIpNames,
    canSend,
    validationErrors,
  };
};

